import type { ReactNode } from 'react';
import { useFieldArray } from 'react-hook-form';
import { AddressFieldset, ChildrenFieldset, EducationFieldset, EmergencyContactFieldset } from '@/features/Collaborators/Edit/Form/Fieldsets';
import { BankAccountFieldSet } from '@/components/shared';
import { DATA_UPDATE_GROUPS, type DataUpdateGroupId } from '../../../../../shared/dpRequests';
import { DATA_UPDATE_GROUP_ICONS } from '../../dataUpdate.definitions';
import { useAuth } from '@/contexts/AuthContext';
import { buildAttachmentPath } from '../../services/dpAttachmentUpload';
import { ContactFields } from './ContactFields';
import { MaritalStatusFields } from './MaritalStatusFields';
import type { DataUpdateSectionsProps } from './dataUpdateSections.types';

export function DataUpdateSections({ groups, form, collaboratorId, isSubmitting, onProofUploaded }: DataUpdateSectionsProps) {
    const { user } = useAuth();
    const ownerUid = user?.uid ?? '';
    const { control, watch, setValue, formState } = form;
    const { errors } = formState;
    const childrenArray = useFieldArray({ control, name: 'children' });
    const educationArray = useFieldArray({ control, name: 'educations' });

    const ordered = DATA_UPDATE_GROUPS.filter(group => groups.includes(group.id));

    function renderGroup(id: DataUpdateGroupId): ReactNode {
        switch (id) {
            case 'address':
                return (
                    <AddressFieldset
                        context="PROFILE_UPDATE"
                        collaboratorId={collaboratorId}
                        control={control}
                        setValue={setValue}
                        watch={watch}
                        errors={errors}
                        isSubmitting={isSubmitting}
                        layout="full"
                        proofUpload={{
                            buildPath: ({ file }) => buildAttachmentPath(ownerUid, file.name),
                            onUploaded: onProofUploaded,
                        }}
                    />
                );
            case 'contact':
                return (
                    <div className="space-y-6">
                        <ContactFields control={control} disabled={isSubmitting} />
                        <EmergencyContactFieldset
                            context="PROFILE_UPDATE"
                            collaboratorId={collaboratorId}
                            control={control}
                            watch={watch}
                            errors={errors}
                            isSubmitting={isSubmitting}
                            layout="compact"
                            isProfileUpdate
                        />
                    </div>
                );
            case 'maritalStatus':
                return <MaritalStatusFields control={control} watch={watch} disabled={isSubmitting} />;
            case 'children':
                return (
                    <ChildrenFieldset
                        context="PROFILE_UPDATE"
                        control={control}
                        errors={errors}
                        isSubmitting={isSubmitting}
                        layout="compact"
                        fields={childrenArray.fields}
                        append={childrenArray.append}
                        remove={childrenArray.remove}
                    />
                );
            case 'bankAccount':
                return (
                    <BankAccountFieldSet
                        context="PROFILE_UPDATE"
                        collaboratorId={collaboratorId}
                        control={control}
                        setValue={setValue}
                        watch={watch}
                        errors={errors}
                        isSubmitting={isSubmitting}
                        layout="full"
                    />
                );
            case 'education':
                return (
                    <EducationFieldset
                        context="FULL_FORM"
                        collaboratorId={collaboratorId}
                        control={control}
                        setValue={setValue}
                        watch={watch}
                        errors={errors}
                        isSubmitting={isSubmitting}
                        layout="compact"
                        fields={educationArray.fields}
                        append={educationArray.append}
                        remove={educationArray.remove}
                    />
                );
        }
    }

    return (
        <div className="space-y-4">
            {ordered.map(group => {
                const Icon = DATA_UPDATE_GROUP_ICONS[group.id];
                return (
                    <section
                        key={group.id}
                        aria-labelledby={`dp-section-${group.id}`}
                        className="rounded-2xl border border-raro-gray-200 bg-white p-5 shadow-sm"
                    >
                        <header className="mb-5 flex items-center gap-3">
                            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                                <Icon className="h-5 w-5" aria-hidden="true" />
                            </span>
                            <h3 id={`dp-section-${group.id}`} className="font-montserrat text-base font-semibold text-raro-gray-900">
                                {group.label}
                            </h3>
                        </header>
                        {renderGroup(group.id)}
                    </section>
                );
            })}
        </div>
    );
}
